import React, { useEffect, useMemo, useState } from "react";
import { SyncModelPermissionEntryWithStatus } from "@/domains/sync-scopes/types";
import { css, cx } from "@/domains/emotion";
import { observer } from "mobx-react-lite";
import { getLabelForRoleKind, getRoleNameForRoleKind } from "@/domains/sync-scopes";
import { FadeInAnimator } from "@/mds/animation";
import { noop } from "lodash-es";
import {
  MDSButtonVariant,
  MDSText,
  MDSTextColor,
  MDSTextVariant,
  MDSIconKind,
  MDSActionMenuDropdown,
  MDSButton,
  MDSMenuContentList,
  MDSMenuContentItem,
  MDSMenuContentItemKind,
  MDSMenuItemSelectionVariant,
  MDSMenuItemVariant,
  MDSIcon,
  MDSIconButton,
  MDSIconButtonVariant,
} from "@/mds";
import styled from "@emotion/styled";

interface ShareSheetPermissionRowLayoutProps {
  isUnsharing: boolean;
  permission: SyncModelPermissionEntryWithStatus;
  children: React.ReactNode;
  canUpdateAccess: boolean;
  canRevokeAccess: boolean;
  handleRevokeAccess: () => void;
  handleUpdateAccessToReadOnly: () => void;
  handleUpdateAccessToReadWrite: () => void;
  handleCancelOperation: (syncOperationId: string) => void;
}

export const ShareSheetPermissionRowLayout = observer<ShareSheetPermissionRowLayoutProps>(
  ({
    permission,
    children,
    isUnsharing,
    canUpdateAccess,
    canRevokeAccess,
    handleRevokeAccess,
    handleUpdateAccessToReadOnly,
    handleUpdateAccessToReadWrite,
    handleCancelOperation,
  }) => {
    const isOwner = permission.role_kind === "OWNER";
    const isReadOnly = permission.role_kind === "VIEWER";
    const isPending = permission.status === "PENDING" || isUnsharing;

    const [isDropdownOpen, setIsDropdownOpen] = useState<boolean>(false);
    const dropdownContentList: MDSMenuContentList = useMemo(() => {
      const output: MDSMenuContentItem[] = [];
      if (canUpdateAccess) {
        output.push({
          id: "edit",
          kind: MDSMenuContentItemKind.Item,
          icon: MDSIconKind.Pen,
          label: `Can edit`,
          isSelected: !isReadOnly,
          selectionVariant: MDSMenuItemSelectionVariant.Checkmark,
          variant: MDSMenuItemVariant.OneLine,
          onClick: isReadOnly ? handleUpdateAccessToReadWrite : handleUpdateAccessToReadOnly,
        });
      }
      if (canRevokeAccess) {
        output.push({
          id: "delete",
          kind: MDSMenuContentItemKind.Item,
          icon: MDSIconKind.Ban,
          label: "Remove access",
          onClick: handleRevokeAccess,
          variant: MDSMenuItemVariant.OneLine,
        });
      }
      return output;
    }, [
      canUpdateAccess,
      canRevokeAccess,
      handleUpdateAccessToReadOnly,
      handleUpdateAccessToReadWrite,
      handleRevokeAccess,
      isReadOnly,
    ]);

    const [showPendingIcon, setShowPendingIcon] = useState<boolean>(false);
    useEffect(() => {
      let timeout: NodeJS.Timeout;
      if (isPending) timeout = setTimeout(() => setShowPendingIcon(true), 1000);
      else setShowPendingIcon(false);
      return () => clearTimeout(timeout);
    }, [isPending]);

    const pendingText = (() => {
      if (isUnsharing && permission.role_kind !== "OWNER") return "Revoking...";
      if (!permission.operationKind) return "Pending...";
      if (permission.operationKind.startsWith("GRANT")) return "Sharing...";
      if (permission.operationKind.startsWith("UPDATE")) return "Updating...";
      if (permission.operationKind.startsWith("REVOKE")) return "Revoking...";
      return "Pending...";
    })();

    const pendingDropdownContentList: MDSMenuContentList = [
      {
        id: "label",
        kind: MDSMenuContentItemKind.Header,
        label: `Updating access to ${getRoleNameForRoleKind(permission.role_kind).toLowerCase()}...`,
      },
      {
        id: "cancel-button",
        kind: MDSMenuContentItemKind.Item,
        label: "Cancel",
        onClick: () =>
          permission.operationId ? handleCancelOperation(permission.operationId) : noop,
        isDisabled: !permission.operationId,
        icon: MDSIconKind.Ban,
      },
    ];

    return (
      <div className={cx(containerStyles, permission.status === "PENDING" && pendingStyles)}>
        <div className={rowWrapperStyles}>{children}</div>
        {isOwner && (
          <StyledOwnerText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
            {getLabelForRoleKind(permission.role_kind)}
          </StyledOwnerText>
        )}
        {!isOwner && isPending && (
          <div className={pendingWrapperStyles}>
            {showPendingIcon && (
              <FadeInAnimator>
                <MDSActionMenuDropdown
                  contentList={pendingDropdownContentList}
                  placement="bottom right"
                >
                  <StyledSmallDropdownButton
                    iconKind={MDSIconKind.InfoCircle}
                    variant={MDSIconButtonVariant.Transparent}
                    isDisabled={!pendingDropdownContentList.length}
                  />
                </MDSActionMenuDropdown>
              </FadeInAnimator>
            )}
            <MDSText variant={MDSTextVariant.TextS} color={MDSTextColor.Secondary}>
              {pendingText}
            </MDSText>
          </div>
        )}
        {!isOwner && !isPending && (
          <MDSActionMenuDropdown
            contentList={dropdownContentList}
            placement="bottom right"
            onOpenChange={setIsDropdownOpen}
          >
            <StyledDropdownButton
              variant={MDSButtonVariant.Transparent}
              isDisabled={!dropdownContentList.length}
            >
              {getLabelForRoleKind(permission.role_kind)}
              <MDSIcon kind={isDropdownOpen ? MDSIconKind.AngleUp : MDSIconKind.AngleDown} />
            </StyledDropdownButton>
          </MDSActionMenuDropdown>
        )}
      </div>
    );
  }
);

const containerStyles = css({
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  gap: "4px",
  margin: "2px 0",
  width: "100%",
});

const pendingStyles = css({
  opacity: 0.5,
});

const rowWrapperStyles = css({
  display: "flex",
  alignItems: "center",
  minWidth: 0,
  overflow: "hidden",
});

const pendingWrapperStyles = css({
  display: "flex",
  alignItems: "center",
  gap: "4px",
});

const StyledSmallDropdownButton = styled(MDSIconButton)`
  padding: 4px;
`;

const StyledDropdownButton = styled(MDSButton)`
  font-weight: ${({ theme }) => theme.fontWeights.regular};
`;

const StyledOwnerText = styled(MDSText)`
  margin-right: 8px;
`;
